import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Form, InputGroup, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getAvailableStores } from '../services/priceService';
import { useHomeLocation } from '../contexts/HomeLocationContext';

const chainDisplayNames = {
  shufersal: 'שופרסל',
  victory: 'ויקטורי',
  carrefour: 'קרפור',
  'rami-levy': 'רמי לוי',
  mega: 'מגה',
  'osher-ad': 'אושר עד',
  'tiv-taam': 'טיב טעם',
  ampm: 'AM:PM',
  keshet: 'קשת',
  yellow: 'יילו',
  freshmarket: 'פרשמרקט',
  'good-pharm': 'גוד פארם',
  'super-pharm': 'סופר פארם',
};

const chainDotClasses = {
  shufersal: 'chain-dot-shufersal',
  victory: 'chain-dot-victory',
  carrefour: 'chain-dot-carrefour',
  'rami-levy': 'chain-dot-rami-levy',
  mega: 'chain-dot-mega',
  'osher-ad': 'chain-dot-osher-ad',
  'tiv-taam': 'chain-dot-tiv-taam',
  ampm: 'chain-dot-ampm',
  keshet: 'chain-dot-keshet',
  yellow: 'chain-dot-yellow',
  freshmarket: 'chain-dot-freshmarket',
  'good-pharm': 'chain-dot-good-pharm',
  'super-pharm': 'chain-dot-super-pharm',
};

const getChainDisplayName = (chainId) => chainDisplayNames[chainId] || chainId;
const getChainDotClass = (chainId) => chainDotClasses[chainId] || 'chain-dot-default';

const DISTANCE_OPTIONS = [
  { label: 'כל המרחקים', value: '' },
  { label: 'עד 1 ק"מ', value: '1' },
  { label: 'עד 2 ק"מ', value: '2' },
  { label: 'עד 5 ק"מ', value: '5' },
  { label: 'עד 10 ק"מ', value: '10' },
  { label: 'עד 20 ק"מ', value: '20' },
];

const StoresBrowser = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChain, setSelectedChain] = useState('');
  const [maxDistance, setMaxDistance] = useState('');
  const { homeLocation, computeDistance } = useHomeLocation();

  useEffect(() => {
    getAvailableStores()
      .then(data => setStores(data))
      .finally(() => setLoading(false));
  }, []);

  // Derive unique chains from loaded stores
  const chains = useMemo(() => {
    const seen = new Set();
    return stores
      .map(s => s.chain)
      .filter(c => c && !seen.has(c) && seen.add(c))
      .sort();
  }, [stores]);

  // Apply custom home location distances when set, otherwise fall back to server-provided values
  const storesWithDistance = useMemo(() => {
    if (!homeLocation) return stores;
    return stores.map(store => {
      const custom = computeDistance(store.lat, store.lon);
      // When custom location is active, stores with no coordinates get null distance
      // so they don't pass distance filters using the old cached value
      if (custom === null) return { ...store, distance_km: null, distance_text: null };
      return {
        ...store,
        distance_km: custom,
        distance_text: `${custom.toFixed(1)} ק"מ`,
      };
    });
  }, [stores, homeLocation, computeDistance]);

  const filtered = useMemo(() => {
    const result = storesWithDistance.filter(store => {
      if (selectedChain && store.chain !== selectedChain) return false;
      if (maxDistance !== '') {
        const max = parseFloat(maxDistance);
        if (store.distance_km === null || store.distance_km === undefined || store.distance_km > max) return false;
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const inName = store.name?.toLowerCase().includes(term);
        const inCity = store.city?.toLowerCase().includes(term);
        const inChain = getChainDisplayName(store.chain).toLowerCase().includes(term);
        if (!inName && !inCity && !inChain) return false;
      }
      return true;
    });
    result.sort((a, b) => {
      const da = a.distance_km ?? Infinity;
      const db = b.distance_km ?? Infinity;
      return da - db;
    });
    return result;
  }, [storesWithDistance, selectedChain, maxDistance, searchTerm]);

  const hasDistanceData = storesWithDistance.some(s => s.distance_km !== null && s.distance_km !== undefined);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2 text-muted">טוען חנויות...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small text-muted mb-1">חיפוש חנות</Form.Label>
              <InputGroup>
                <Form.Control
                  placeholder="שם חנות, עיר..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  dir="rtl"
                />
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>✕</Button>
                )}
              </InputGroup>
            </Col>
            <Col md={4}>
              <Form.Label className="small text-muted mb-1">רשת</Form.Label>
              <Form.Select value={selectedChain} onChange={e => setSelectedChain(e.target.value)}>
                <option value="">כל הרשתות</option>
                {chains.map(chain => (
                  <option key={chain} value={chain}>{getChainDisplayName(chain)}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted mb-1">
                מרחק מהבית{' '}
                {homeLocation && <span className="badge bg-primary ms-1" style={{ fontSize: '11px', fontWeight: 500 }}>מיקום מותאם</span>}
                {!hasDistanceData && !homeLocation && <span className="text-warning">(אין נתוני מרחק)</span>}
              </Form.Label>
              <Form.Select
                value={maxDistance}
                onChange={e => setMaxDistance(e.target.value)}
                disabled={!hasDistanceData}
              >
                {DISTANCE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={1} className="d-flex align-items-end">
              {(searchTerm || selectedChain || maxDistance) && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => { setSearchTerm(''); setSelectedChain(''); setMaxDistance(''); }}
                  title="נקה פילטרים"
                >
                  נקה
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Results count */}
      <div style={{ fontSize: 'var(--font-caption)', color: 'var(--apple-text-3)', marginBottom: 12 }}>
        {filtered.length === stores.length
          ? `${stores.length.toLocaleString()} חנויות`
          : `${filtered.length.toLocaleString()} מתוך ${stores.length.toLocaleString()} חנויות`}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-5" style={{ color: 'var(--apple-text-2)' }}>
          <p>לא נמצאו חנויות התואמות את הפילטרים</p>
        </div>
      ) : (
        <Row xs={1} sm={2} md={3} lg={4} className="g-3">
          {filtered.map((store) => (
            <Col key={`${store.chain}-${store.id}`}>
              <Link
                to={`/store/${store.id}?chain=${store.chain || ''}`}
                style={{ textDecoration: 'none', display: 'block', height: '100%' }}
              >
                <Card className="h-100 store-card">
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <span className="chain-badge">
                        <span className={`chain-dot ${getChainDotClass(store.chain)}`}></span>
                        {getChainDisplayName(store.chain)}
                      </span>
                      {store.distance_text && (
                        <span style={{ fontSize: 'var(--font-caption)', color: 'var(--apple-text-3)' }}>
                          {store.distance_text}
                        </span>
                      )}
                    </div>
                    <div dir="rtl" style={{ fontWeight: 600, marginBottom: 4, lineHeight: 1.3, flex: 1, color: 'var(--apple-text)' }}>
                      {store.name}
                    </div>
                    {store.city && (
                      <div dir="rtl" style={{ fontSize: 'var(--font-caption)', color: 'var(--apple-text-2)' }}>
                        {store.city}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default StoresBrowser;
